'use client';
import { get, put } from '@nineeng/request';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { UserPreference, VisualSettings } from '@repo/shared/domain';

const defaultVisualSettings: VisualSettings = {
	userMetrics: true,
	dailyUsersChart: true,
	complianceSection: true,
	eventOverviewSection: true,
	inhoudingsplicht: true,
};

const UserPreferencesContext = createContext<{
	userPreferences: UserPreference;
	loading: boolean;
	updateUserPreferences: (_key: keyof UserPreference, _value: boolean | VisualSettings) => void;
}>({
	userPreferences: {
		visualSettings: defaultVisualSettings,
	},
	loading: true,
	updateUserPreferences: () => {},
});

export const UserPreferenceProvider = ({ children }: { children: React.ReactNode }) => {
	const [loading, setLoading] = useState(true);
	const [userPreferences, setUserPreference] = useState<UserPreference>({
		visualSettings: defaultVisualSettings,
	});

	useEffect(() => {
		const fetchUserPreferences = async () => {
			const { body: userPreferences } = await get<UserPreference>('/api/user-preferences');
			setUserPreference({
				...userPreferences,
				visualSettings: userPreferences.visualSettings || defaultVisualSettings,
			});
			setLoading(false);
		};
		fetchUserPreferences();
	}, []);

	const updateUserPreferences = useCallback(async (key: keyof UserPreference, value: boolean | VisualSettings) => {
		setUserPreference((prevPrefs) => {
			const newSettings = {
				...prevPrefs,
				[key]: value,
			};
			return newSettings as UserPreference;
		});

		await put('/api/user-preferences', { [key]: value });
	}, []);

	return (
		<UserPreferencesContext.Provider value={{ userPreferences, loading, updateUserPreferences }}>
			{children}
		</UserPreferencesContext.Provider>
	);
};

export const useUserPreferences = () => useContext(UserPreferencesContext);
